import { useQuery } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCurrentUserQueryVariables,
  useCurrentUserProfile,
} from 'src/graphql/entities/platform-user';
import { GET_MY_CROSS_PARTNER_USER_SETTINGS_QUERY } from 'src/graphql/entities/side-panel-personalization/side-panel-personalization.operations';
import { useCreateUserSettingValue } from 'src/graphql/entities/user-setting-value/hooks/use-create-user-setting-value';
import { useUpdateUserSettingValue } from 'src/graphql/entities/user-setting-value/hooks/use-update-user-setting-value';
import {
  UserSettingId,
  UserSettingValue,
} from 'src/graphql/entities/user-setting-value/user-setting-value.types';

interface GetMyCrossPartnerUserSettingsQuery {
  getPlatformUser: {
    records: { userSettingValues: UserSettingValue[] }[];
  } | null;
}

export interface CrossPartnerUserSettings {
  /** raw stored value per setting id, or `undefined` when the user has none yet */
  values: Partial<Record<UserSettingId, string>>;
  setValue: (id: UserSettingId, serialized: string, valueType: string) => void;
  loading: boolean;
}

/**
 * Reads/writes the current user's own side-panel settings across every partner they
 * can access, so a personalized layout follows them between tenants (1V-5371).
 *
 * The read runs unscoped (`omitIsolationModeHeader`) and `no-cache` so it never
 * pollutes the partner-scoped current-user cache that every other setting relies on.
 * Writes already run unscoped (see `useUpdateUserSettingValue`); once a row is known
 * (from any partner), `setValue` updates it in place — the update matches on
 * `userId`+`userSettingId`, not partner — instead of creating a per-partner duplicate.
 */
export function useCrossPartnerUserSettings(): CrossPartnerUserSettings {
  const { user } = useCurrentUserProfile();
  const { createUserSettingValue } = useCreateUserSettingValue();
  const { updateUserSettingValue } = useUpdateUserSettingValue();

  const { data, loading } = useQuery<GetMyCrossPartnerUserSettingsQuery>(
    GET_MY_CROSS_PARTNER_USER_SETTINGS_QUERY,
    {
      variables: getCurrentUserQueryVariables(),
      context: { omitIsolationModeHeader: true, ttl: 0 },
      fetchPolicy: 'no-cache',
      skip: !user,
    },
  );

  const serverRows = useMemo(
    () => data?.getPlatformUser?.records?.[0]?.userSettingValues ?? [],
    [data],
  );

  // optimistic values keep edits instant (the no-cache read is not reactive to writes)
  const [localValues, setLocalValues] = useState<Partial<Record<UserSettingId, string>>>({});
  // the row each setting maps to (server- or session-created) so writes target it
  const rowsRef = useRef<Partial<Record<UserSettingId, UserSettingValue>>>({});

  useEffect(() => {
    for (const row of serverRows) {
      rowsRef.current[row.userSettingId as UserSettingId] = row;
    }
  }, [serverRows]);

  const values = useMemo(() => {
    const merged: Partial<Record<UserSettingId, string>> = {};
    for (const row of serverRows) {
      merged[row.userSettingId as UserSettingId] = row.value;
    }
    return { ...merged, ...localValues };
  }, [serverRows, localValues]);

  const setValue = useCallback(
    (id: UserSettingId, serialized: string, valueType: string) => {
      setLocalValues((prev) => ({ ...prev, [id]: serialized }));

      const row = rowsRef.current[id];
      if (row) {
        updateUserSettingValue(row, { value: serialized });
      } else {
        createUserSettingValue(
          { userSettingId: id, value: serialized, valueType },
          (created) => {
            rowsRef.current[id] = created;
          },
        );
      }
    },
    [createUserSettingValue, updateUserSettingValue],
  );

  return { values, setValue, loading: loading && !data };
}
